// NATIVE
import React, { useEffect, useMemo, useState } from 'react';

// INSTALLED
import Box from '@mui/material/Box';
import { Formik, Form } from 'formik';
import Step from '@mui/material/Step';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// CUSTOM
import Stepper from '@mui/material/Stepper';
import StepLabel from '@mui/material/StepLabel';
import { SnackAlert } from 'components/SnackAlert';
import { InformacionBasica } from './components';
import { Familia } from './components/stepers/steper-crear/Familia';
import { Laboral } from './components/stepers/steper-crear/Laboral';
import MultipleSnackbars from 'components/MultipleSnackbars';
import { Dotacion } from './components/stepers/steper-crear/Dotacion';
import { getEmployeDataCreateStore, saveEmployee, storeDetailEmployee } from 'store/employee';
import { PruebasIngreso } from './components/stepers/steper-crear/PruebasIngreso';
import { Breadcrumbs, Button, Card, Divider, Typography, Alert } from '@mui/material';
import { SeguridadSocial } from './components/stepers/steper-crear/SeguridadSocial';
import validationSchema from './formik/crear/validationSchema';
import formInitialValues from './formik/crear/formInitialValues';
import createEmployeFormModel from './formik/crear/createEmployeFormModel';
import { BreadCrumbsAndTitle } from 'components/generics/BreadCrumbsAndTitle';
import { mtDividerUpTable, timeToClosedToast } from '../../../commons';
import { ModalSessionExpired } from 'components/generics/ModalSessionExpired';
import { closeSessionGlobal, hasPemissions, validateResponse } from 'commons/utils';
import { uploadFileImageFromCamera } from 'store/uploaders';
import { useResponseHandler } from 'hooks/useResponseHandler';
import { ModalViewPdfGlobal } from 'components/modals/viwerPdf/ModalViewPdfGlobal';

export const CreateEmployee = () => {
  // USE DISPATCH
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const goToEmployee = async (id: string) => {
    await dispatch(storeDetailEmployee(id));
    navigate('/talento-humano/empleados/detalle');
  };

  // DATA MODEL
  const { formId, formField } = createEmployeFormModel;

  // USE STATE
  const [data, setMsgErrors] = useState([]);

  const [activeStep, setActiveStep] = useState(0);
  const [percentaje_c_Company, setPercentajeCCompany] = useState(2);

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [btnText, setBtnText] = React.useState('Crear Empleado');

  const { handleResponse, ResponseComponents, setMsgSnack, setAlertSnack } = useResponseHandler();

  // Estado global del modal PDF
  const [showPdfDetail, setShowPdfDetail] = useState(false);
  const [urlRutPdf, setUrlRutPdf] = useState<string | null>(null);
  const [typeUrl, setTypeUrl] = useState<string | null>(null);

  const handleOpenPdf = (url: string, urlTypeUrl: string) => {
    setUrlRutPdf(url);
    setTypeUrl(urlTypeUrl);
    setShowPdfDetail(true);
  };

  const currentValidationSchema = validationSchema[activeStep];

  const initialValues = useMemo(
    () => ({
      ...formInitialValues,
      family: [],
      items: [],
      with_photo: true,
      with_imagen: false,
      imagenEmployeeToSave: null,
      imagenEmployeeToSaveUpload: null,
      employee_avatar: null,
    }),
    []
  );

  // STEPS
  const steps: string[] = [
    'Datos Basicos',
    'Laboral',
    'Seguridad social',
    'Familia',
    'Dotacion',
    'Pruebas de ingreso',
  ];

  const isLastStep = activeStep === steps.length - 1;

  // USE EFFECT
  useEffect(() => {
    getDataInpusToCreateEmploye();
  }, []);

  const getDataInpusToCreateEmploye = async () => {
    const response = await dispatch(getEmployeDataCreateStore());
  };

  const lastStep = () => {
    if (activeStep == 0) return;

    setActiveStep(activeStep - 1);
  };

  const _handleSubmit = (values: any, actions: any) => {
    if (isLastStep) {
      _submitForm(values, actions);
    } else {
      setActiveStep(activeStep + 1);
      actions.setTouched({});
      actions.setSubmitting(false);
    }
  };

  const _renderStepContent = (stepIndex: any) => {
    return (
      <>
        <div style={{ display: stepIndex === 0 ? 'block' : 'none' }}>
          <InformacionBasica formField={formField} onOpenPdf={handleOpenPdf} />
        </div>
        <div style={{ display: stepIndex === 1 ? 'block' : 'none' }}>
          <Laboral formField={formField} onOpenPdf={handleOpenPdf} />
        </div>
        <div style={{ display: stepIndex === 2 ? 'block' : 'none' }}>
          <SeguridadSocial formField={formField} onOpenPdf={handleOpenPdf} />
        </div>
        <div style={{ display: stepIndex === 3 ? 'block' : 'none' }}>
          <Familia formField={formField} onOpenPdf={handleOpenPdf} />
        </div>
        <div style={{ display: stepIndex === 4 ? 'block' : 'none' }}>
          <Dotacion formField={formField} />
        </div>
        <div style={{ display: stepIndex === 5 ? 'block' : 'none' }}>
          <PruebasIngreso formField={formField} onOpenPdf={handleOpenPdf} />
        </div>
      </>
    );
  };

  const _submitForm = async (values: any, actions: any) => {
    const havePermissions = await hasPemissions('modulo.thumano', 'modulo.empleados', 'create');

    if (!havePermissions) {
      await validateLsPermissions('No tienes pemisos para (Crear) empleado.');

      return false;
    }

    const imageBlob = values.imagenEmployeeToSave || values.imagenEmployeeToSaveUpload;

    if (!imageBlob) {
      const errors: any = {
        msg: 'La foto del empleado es obligatoria. Por favor toma una foto o sube una imagen en Información Básica.',
        status: 'error',
      };

      await setMsgSnack(errors);
      await setAlertSnack(true);
      return false;
    }

    const responseImage = await dispatch(
      uploadFileImageFromCamera('avatar-employee', imageBlob, setPercentajeCCompany)
    );

    if (!responseImage || !responseImage.ok || !responseImage.data?.imgName) {
      const errors: any = {
        msg: responseImage?.message || 'Error al subir la imagen del empleado',
        status: 'error',
      };

      await setMsgSnack(errors);
      await setAlertSnack(true);
      return false;
    }

    const valuesToSubmit = {
      ...values,
      employee_avatar: responseImage.data.imgName,
    };

    actions.setSubmitting(true);
    setIsSubmitting(true);
    setBtnText('Un momento...');

    const response = await dispatch(saveEmployee(valuesToSubmit, setPercentajeCCompany));

    setIsSubmitting(false);
    setBtnText('Crear Empleado');

    actions.setSubmitting(false);

    await handleResponse(response, 'Crear empleado', true);

    if (!response.ok) {
      return false;
    }

    if (response.ok) {
      const errors: any = {
        msg: response.message || 'Empleado creado con éxito',
        status: 'success',
      };

      await setMsgSnack(errors);
      await setAlertSnack(true);

      actions.setSubmitting(false);

      const newEmployeeId = response.data?.id || response.data?.empleadoId || response.data?._id;
      if (newEmployeeId) {
        actions.setFieldValue('existingEmployeeId', newEmployeeId);
      }
    }
  };

  const validateLsPermissions = async (message: string) => {
    const errors: any = {
      msg: message,
      status: 'error',
    };

    await setMsgSnack(errors);
    await setAlertSnack(true);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {ResponseComponents}

      {showPdfDetail && (
        <ModalViewPdfGlobal
          open={showPdfDetail}
          typeUrl={typeUrl}
          onClose={() => setShowPdfDetail(false)}
          pdfUrl={urlRutPdf}
        />
      )}

      <BreadCrumbsAndTitle
        title="Crear Empleado"
        mainModule="Talento Humano"
        parent="Listar Empleados"
        action="/talento-humano/empleados"
      />

      <Divider sx={{ marginBottom: mtDividerUpTable }} />

      <Stepper activeStep={activeStep}>
        {steps.map((label: any, index: any) => {
          const labelProps: {
            optional?: React.ReactNode;
            error?: boolean;
          } = {};
          // if (isStepFailed(index)) {
          //   labelProps.optional = (
          //     <Typography variant="caption" color="error">
          //       Faltan campos por diligenciar
          //     </Typography>
          //   );
          //   labelProps.error = true;
          // }

          return (
            <Step key={label}>
              <StepLabel {...labelProps}>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>

      <Formik
        initialValues={initialValues}
        validationSchema={currentValidationSchema}
        onSubmit={_handleSubmit}
      >
        {({ values, errors, submitCount }: any) => (
          <Card style={{ marginTop: 50, padding: 20 }}>
            <Form id={formId} autoComplete="off" noValidate>
              {_renderStepContent(activeStep)}
              {submitCount > 0 && errors && Object.keys(errors).length > 0 && (
                <Alert severity="error" sx={{ marginTop: 3 }}>
                  Revisa los siguientes campos:
                  <ul style={{ margin: '4px 0 0 0' }}>
                    {Object.entries(errors).map(([key, err]: any) => {
                      const message =
                        typeof err === 'string'
                          ? err
                          : (Object.values(err || {}).find(
                              (v) => typeof v === 'string'
                            ) as string) || `${key} es requerido.`;
                      return <li key={key}>{String(message)}</li>;
                    })}
                  </ul>
                </Alert>
              )}
              <div style={{ display: 'flex', justifyContent: 'end', marginTop: 25 }}>
                {activeStep > 0 && (
                  <Button
                    style={{ margin: 5 }}
                    variant="contained"
                    onClick={lastStep}
                    color="primary"
                  >
                    Atras
                  </Button>
                )}

                {activeStep === 0 && values.existingEmployeeId ? (
                  <Alert
                    severity="warning"
                    sx={{ margin: '5px', py: 0, px: 2, alignItems: 'center' }}
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => goToEmployee(values.existingEmployeeId)}
                      >
                        Ver Empleado
                      </Button>
                    }
                  >
                    Ya registrado
                  </Alert>
                ) : (
                  <Button
                    disabled={isSubmitting}
                    style={{ margin: 5 }}
                    type="submit"
                    variant="contained"
                    color={isLastStep ? 'success' : 'primary'}
                  >
                    {isLastStep ? btnText : 'Siguiente'}
                  </Button>
                )}
              </div>
            </Form>
          </Card>
        )}
      </Formik>
    </Box>
  );
};
